import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { CUSTOM_URL, FUSION_PREFIX } from 'services/_infra/environment';

/**
 * RTK Query API for Doppler map tiles.
 *
 * Note: Map tiles are typically fetched directly by Leaflet's TileLayer via URL,
 * which handles its own caching. This API is useful for:
 * - Prefetching tiles before they're needed
 * - Centralized cache invalidation
 * - Tracking loading/error states in Redux
 * - Custom tile caching strategies
 */
export const dopplerApi = createApi({
  reducerPath: 'dopplerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CUSTOM_URL}${FUSION_PREFIX}`,
    prepareHeaders: (headers) => {
      const token = Cookies.get('accesstoken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['MapTile'],
  endpoints: (builder) => ({
    /**
     * Fetch a single map tile.
     * @param {Object} params - Tile parameters
     * @param {string} params.baseTime - Rounded base time ISO string
     * @param {string} params.frameTime - Frame time ISO string
     * @param {string} params.layer - Layer type (e.g., 'future_radar')
     * @param {number} params.z - Zoom level
     * @param {number} params.x - Tile X coordinate
     * @param {number} params.y - Tile Y coordinate
     */
    getMapTile: builder.query({
      query: ({ baseTime, frameTime, layer, z, x, y }) => ({
        url: `getMapTileExperimental/${baseTime}/${frameTime}/${layer}/${z}/${x}/${y}`,
        responseHandler: async (response) => {
          // Return blob URL for image tiles
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
      providesTags: (result, error, { baseTime, frameTime, layer, z, x, y }) => [
        { type: 'MapTile', id: `${layer}-${z}-${x}-${y}-${frameTime}` },
      ],
      // Keep tiles cached for 5 minutes
      keepUnusedDataFor: 300,
    }),

    /**
     * Prefetch multiple tiles for a frame (useful for animation preloading).
     * This is a query that returns tile URLs for a given viewport.
     */
    prefetchFrameTiles: builder.query({
      query: ({ baseTime, frameTime, layer, bounds, zoom }) => ({
        url: `getMapTileExperimental/info`,
        method: 'POST',
        body: { baseTime, frameTime, layer, bounds, zoom },
      }),
      // This would require a backend endpoint that returns tile info
      // For now, this is a placeholder for future optimization
    }),
  }),
});

// Export hooks for usage in components
export const { useGetMapTileQuery, usePrefetchFrameTilesQuery, useLazyGetMapTileQuery } =
  dopplerApi;

/**
 * Helper to generate the tile URL for Leaflet TileLayer.
 * Use this when you want Leaflet to handle fetching directly.
 *
 * @param {Date} baseTime - Rounded base time
 * @param {Date} frameTime - Frame time
 * @param {string} layer - Layer type (default: 'future_radar')
 * @returns {string} URL template for TileLayer
 */
export function getMapTileUrl(baseTime, frameTime, layer = 'future_radar') {
  return `${CUSTOM_URL}${FUSION_PREFIX}getMapTileExperimental/${baseTime.toISOString()}/${frameTime.toISOString()}/${layer}/{z}/{x}/{y}`;
}

/**
 * Helper to round a date to nearest 10 minutes (for cache key consistency).
 * @param {Date} date
 * @returns {Date}
 */
export function roundToNearestTenMinutes(date) {
  const coeff = 1000 * 60 * 10;
  return new Date(Math.round(date.getTime() / coeff) * coeff);
}

/**
 * Generate frame times for the animation (12 frames, 10 min apart).
 * @param {number} frameCount - Number of frames (default: 12)
 * @param {number} intervalMinutes - Minutes between frames (default: 10)
 * @returns {Date[]} Array of frame times
 */
export function generateFrameTimes(frameCount = 12, intervalMinutes = 10) {
  const baseTime = roundToNearestTenMinutes(new Date());
  return Array.from({ length: frameCount }, (_, i) => {
    const frameTime = new Date(baseTime);
    frameTime.setMinutes(frameTime.getMinutes() + i * intervalMinutes);
    return frameTime;
  });
}

export default dopplerApi;
